import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Cadence, InstalmentInput, PaymentPlan } from '../types';
import { useTranslation } from '../i18n/TranslationContext';

interface InstalmentsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (instalments: InstalmentInput[]) => void;
  plan: PaymentPlan;
  totalAmount: number;
  paymentMethod: string;
}

const cadenceLabels: Record<Cadence, string> = {
  weekly: 'settimanale',
  biweekly: 'bisettimanale',
  monthly: 'mensile',
  bimonthly: 'bimestrale',
  quarterly: 'trimestrale',
  semiannual: 'semestrale',
  annual: 'annuale',
};

const addCadence = (date: Date, cadence: Cadence, n: number): Date => {
  const d = new Date(date.getTime());
  switch (cadence) {
    case 'weekly': d.setDate(d.getDate() + 7 * n); break;
    case 'biweekly': d.setDate(d.getDate() + 14 * n); break;
    case 'monthly': d.setMonth(d.getMonth() + n); break;
    case 'bimonthly': d.setMonth(d.getMonth() + 2 * n); break;
    case 'quarterly': d.setMonth(d.getMonth() + 3 * n); break;
    case 'semiannual': d.setMonth(d.getMonth() + 6 * n); break;
    case 'annual': d.setFullYear(d.getFullYear() + n); break;
  }
  return d;
};

const toDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const daysBetween = (a: string, b: string): number => {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((da.getTime() - db.getTime()) / (1000 * 60 * 60 * 24));
};

const computeAmounts = (total: number, count: number, distributions: PaymentPlan['distributions']): number[] => {
  const custom = distributions?.[String(count)];
  if (custom && custom.length === count) {
    const amounts = custom.map(p => Math.round((total * p / 100) * 100) / 100);
    const sum = amounts.reduce((a, b) => a + b, 0);
    const diff = Math.round((total - sum) * 100) / 100;
    if (Math.abs(diff) > 0) {
      amounts[amounts.length - 1] = Math.round((amounts[amounts.length - 1] + diff) * 100) / 100;
    }
    return amounts;
  }
  const equal = Math.round((total / count) * 100) / 100;
  const amounts = Array(count).fill(equal);
  const sum = equal * count;
  const diff = Math.round((total - sum) * 100) / 100;
  if (Math.abs(diff) > 0) {
    amounts[amounts.length - 1] = Math.round((amounts[amounts.length - 1] + diff) * 100) / 100;
  }
  return amounts;
};

const InstalmentsDialog: React.FC<InstalmentsDialogProps> = ({ open, onClose, onConfirm, plan, totalAmount, paymentMethod }) => {
  const { t, fmtMoney } = useTranslation();
  const cadenceLabel = (c: Cadence) => t(`rate.cadenza_${c}`, cadenceLabels[c] ?? c);
  const isOnline = paymentMethod === 'paypal' || paymentMethod === 'stripe';

  const minN = Math.max(1, plan.min_instalments);
  const maxN = Math.max(minN, plan.max_instalments);

  const allowSingle = !!plan.allow_single;
  const startN = allowSingle ? 1 : Math.max(2, minN);

  const [mode, setMode] = useState<'single' | 'instalments'>(allowSingle ? 'single' : 'instalments');
  const [count, setCount] = useState<number>(startN > 1 ? startN : 2);
  const [dates, setDates] = useState<string[]>([]);
  const [error, setError] = useState<string>('');

  // Date fissate dall'organizzatore: l'acconto si paga subito, le rate successive
  // partono dalla data di riferimento (±flessibilità) invece che dalla frequenza
  const fixedDates = plan.due_dates?.[String(count)] ?? null;
  const firstToday = isOnline || !!fixedDates;
  const expectedDate = (i: number, first: string) =>
    fixedDates?.[i] ?? toDateString(addCadence(new Date(first + 'T00:00:00'), plan.cadence, i));

  useEffect(() => {
    if (!open) return;
    setError('');
    setMode(allowSingle ? 'single' : 'instalments');
    const initialN = allowSingle ? 1 : Math.max(2, minN);
    setCount(initialN);
  }, [open, allowSingle, minN]);

  useEffect(() => {
    if (mode === 'single') {
      setDates([toDateString(new Date())]);
      return;
    }
    const first = toDateString(new Date());
    const next: string[] = [];
    for (let i = 0; i < count; i++) {
      next.push(i === 0 ? first : expectedDate(i, first));
    }
    setDates(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, count, plan.cadence, plan.due_dates]);

  const amounts = useMemo(() => {
    if (mode === 'single') return [totalAmount];
    return computeAmounts(totalAmount, count, plan.distributions);
  }, [mode, count, totalAmount, plan.distributions]);

  const today = useMemo(() => toDateString(new Date()), []);

  const validate = (): InstalmentInput[] | null => {
    if (mode === 'single') {
      const dateOnly = isOnline ? today : (dates[0] || today);
      return [{ sequence: 1, amount: totalAmount, due_date: dateOnly }];
    }

    const res: InstalmentInput[] = [];
    const flex = plan.flexibility_days || 0;

    for (let i = 0; i < count; i++) {
      const date = dates[i];
      if (!date) {
        setError(t('rate.err_data', 'Imposta una data per la rata {{n}}', { n: i + 1 }));
        return null;
      }
      if (i === 0) {
        if (firstToday && date !== today) {
          setError(t('rate.err_prima_oggi', 'La rata 1 deve essere oggi ({{data}}) per il pagamento online', { data: today }));
          return null;
        }
      } else {
        const expected = expectedDate(i, dates[0]);
        const diff = Math.abs(daysBetween(date, expected));
        if (diff > flex) {
          setError(t('rate.err_flessibilita', 'La rata {{n}} deve essere entro ±{{flex}} giorni dalla data prevista ({{data}})', { n: i + 1, flex, data: expected }));
          return null;
        }
      }
      res.push({ sequence: i + 1, amount: amounts[i], due_date: date });
    }

    const sum = res.reduce((a, b) => a + b.amount, 0);
    if (Math.abs(sum - totalAmount) > 0.01) {
      setError(t('rate.err_somma', 'La somma delle rate non corrisponde al totale'));
      return null;
    }
    return res;
  };

  const handleConfirm = () => {
    setError('');
    const result = validate();
    if (result) onConfirm(result);
  };

  const updateDate = (idx: number, value: string) => {
    setDates(prev => {
      const next = [...prev];
      next[idx] = value;
      if (idx === 0 && mode === 'instalments' && !fixedDates) {
        for (let i = 1; i < count; i++) {
          next[i] = expectedDate(i, value);
        }
      }
      return next;
    });
  };

  const counts = useMemo(() => {
    const arr: number[] = [];
    const start = Math.max(2, minN);
    for (let n = start; n <= maxN; n++) arr.push(n);
    return arr;
  }, [minN, maxN]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('rate.titolo', 'Modalità di pagamento')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('rate.importo_totale', 'Importo totale:')} <strong>{fmtMoney(totalAmount)}</strong>
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {allowSingle && counts.length > 0 && (
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as 'single' | 'instalments')}
            sx={{ mb: 2 }}
          >
            <FormControlLabel value="single" control={<Radio />} label={t('rate.unica_soluzione', 'Pagamento in unica soluzione')} />
            <FormControlLabel value="instalments" control={<Radio />} label={t('rate.a_rate', 'Pagamento a rate')} />
          </RadioGroup>
        )}

        {mode === 'instalments' && counts.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('rate.numero', 'Numero di rate')}</InputLabel>
            <Select value={count} label={t('rate.numero', 'Numero di rate')} onChange={(e) => setCount(Number(e.target.value))}>
              {counts.map(n => (
                <MenuItem key={n} value={n}>
                  {plan.due_dates?.[String(n)]
                    ? t('rate.n_rate_date', '{{n}} rate (acconto + scadenze fisse)', { n })
                    : t('rate.n_rate', '{{n}} rate ({{cadenza}})', { n, cadenza: cadenceLabel(plan.cadence) })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {mode === 'single' && (
          <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2">
              {isOnline
                ? t('rate.immediato', "Procederai con il pagamento immediato dell'intero importo.")
                : t('rate.data_bonifico', 'Indica la data prevista per il bonifico.')}
            </Typography>
            {!isOnline && (
              <TextField
                type="date"
                fullWidth
                label={t('rate.data_prevista', 'Data prevista pagamento')}
                value={dates[0] || today}
                onChange={(e) => setDates([e.target.value])}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        )}

        {mode === 'instalments' && dates.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {fixedDates
                ? t('rate.date_fisse', "L'acconto si versa all'iscrizione. Le scadenze delle rate successive sono indicate dall'organizzatore: puoi spostarle entro ±{{flex}} giorni.", { flex: plan.flexibility_days })
                : t('rate.date_auto', 'Le date sono autoimpostate in base alla frequenza {{cadenza}}. Puoi modificarle entro ±{{flex}} giorni rispetto alla data calcolata.', { cadenza: cadenceLabel(plan.cadence), flex: plan.flexibility_days })}
              {isOnline && ` ${t('rate.prima_oggi', 'La prima rata viene pagata oggi al checkout.')}`}
            </Typography>
            {dates.map((d, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    type="date"
                    fullWidth
                    label={t('rate.rata_n', 'Rata {{n}}', { n: idx + 1 })}
                    value={d}
                    onChange={(e) => updateDate(idx, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={idx === 0 && firstToday}
                    helperText={idx === 0 && firstToday
                      ? (isOnline ? t('rate.pagata_oggi', 'Pagata oggi al checkout') : t('rate.acconto_oggi', "Acconto da versare all'iscrizione"))
                      : undefined}
                  />
                </Box>
                <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                  <Typography variant="subtitle2">{fmtMoney(amounts[idx] || 0)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>{t('common.annulla', 'Annulla')}</Button>
        <Button variant="contained" onClick={handleConfirm}>
          {isOnline && mode === 'single' ? t('pagamento.procedi', 'Procedi al pagamento') : t('common.conferma', 'Conferma')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstalmentsDialog;
